import '../../common/EsCommon.css';
import './EsAccount.css';

import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { API_LOGIN_BIRTHDATE, VIEW_LOGIN, VIEW_WELCOME } from '../../common/EsConstants';
import { esHardNavigate } from '../../common/EsEnv';
import { esPost } from '../../common/EsFetch';
import { esLoginDestination } from './EsLoginDestination';
import { useScrollIntoViewWhen } from '../../common/EsScrollIntoView';
import { useEsStrings } from '../../common/EsStrings';
import EsBirthdateFields, { esBirthdateIso, esBirthdateRefusal } from './EsBirthdateFields';

/**
 * The birthdate step (Tim, 2026-10-14): a login whose account has no birthdate -- an account
 * from before V084, or a Google sign-up -- is asked here, after its password or Google proved
 * who it is and before the login completes.
 *
 * <p>esback holds the login (EsPendingLogin) and starts no session until API_LOGIN_BIRTHDATE
 * takes a birthdate, so leaving this page leaves the reader logged out. Whose birthdate it is
 * comes from that held login, never from this page, and the age is esback's to judge.
 *
 * <p>Where it goes after: the page a Google login had saved (the answer's path), else the page
 * a form login was sent from (router state), else welcome. A HARD navigation, for EsLogin's
 * reason: AuthProvider reads authUser once at mount.
 */
export default function EsBirthdate() {
    const t = useEsStrings();
    const location = useLocation();

    const [ bday, setBday ] = useState( '' );
    const [ error, setError ] = useState( null );
    // The step is over without a login: timed out, opened cold, turned away, or disabled.
    const [ ended, setEnded ] = useState( null );

    const errorRef = useScrollIntoViewWhen( Boolean( error || ended ) );

    const handleSubmit = async ( pEvent ) => {
        pEvent.preventDefault();

        const aRefusal = esBirthdateRefusal( bday, new Date(), t );
        setError( aRefusal );
        if ( aRefusal ) return;

        try {
            const aAnswer = await esPost( API_LOGIN_BIRTHDATE, { bday: esBirthdateIso( bday ) } );
            esHardNavigate( aAnswer?.path || esLoginDestination( location ) || VIEW_WELCOME );
        }
        catch ( err ) {
            // A Google sign-up that admit refused carries the register page to show it on.
            if ( err.body?.path ) esHardNavigate( err.body.path );
            else if ( err.status === 401 ) setEnded( t( 'account-birthdate-expired', 'That login has timed out.' ) );
            else if ( err.body?.outcome ) {
                setBday( '' );
                setEnded( err.message );
            }
            else setError( err.message || t( 'common-server-unreachable', 'Sorry — could not reach the server.' ) );
        }
    };

    return (
        <section data-testid="es-birthdate-page" className="relative z-10 pb-4 pt-nav-room">
            <div className="container">
                <div className="flex flex-wrap mx-[-16px]">
                    <div className="w-full px-4">
                        <div className="mx-auto rounded-md p-12 max-w-[500px] sm:p-[60px]">
                            <h3 data-testid="es-birthdate-heading" className="mb-3 text-center text-2xl font-bold text-heading sm:text-3xl">
                                { t( 'account-birthdate-heading', 'One more thing' ) }
                            </h3>
                            { ended
                                ? <p ref={ errorRef } data-testid="es-birthdate-ended" className="formError">
                                      { t( 'common-sentences', '{first} {second}', { first: ended, second:
                                          <Link to={ VIEW_LOGIN } data-testid="es-birthdate-login-link"
                                                className="whitespace-nowrap text-accent hover:underline">
                                              { t( 'account-birthdate-to-login', 'Back to login' ) }
                                          </Link> } ) }
                                  </p>
                                : <>
                                      <p data-testid="es-birthdate-blurb" className="formSubHeader">
                                          { t( 'account-birthdate-blurb', 'Add your birthdate to finish logging in.' ) }
                                      </p>
                                      <form data-testid="es-birthdate-form" onSubmit={ handleSubmit }>
                                          <EsBirthdateFields idPrefix="es-birthdate" bday={ bday } onChange={ setBday } />
                                          <div className="mb-6">
                                              <button type="submit" data-testid="es-birthdate-submit-button"
                                                      className="buttonSubmit flex w-full items-center justify-center">
                                                  { t( 'account-birthdate-submit', 'Continue' ) }
                                              </button>
                                          </div>
                                          { error &&
                                              <p ref={ errorRef } data-testid="es-birthdate-error" className="formError">{ error }</p> }
                                      </form>
                                  </> }
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
